import { useEffect, useMemo, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatAud } from "../../lib/api";
import StoreHeader  from "../../components/layout/StoreHeader";
import StoreFooter  from "../../components/layout/StoreFooter";
import ProductCard  from "../../components/ProductCard";
import { X, ChevronLeft, ChevronRight, SlidersHorizontal, ChevronDown, Search, Car, LayoutGrid } from "lucide-react";

const SORTS = [
  { value: "name",       label: "Name A–Z" },
  { value: "newest",     label: "Newest first" },
  { value: "price_asc",  label: "Price: Low to High" },
  { value: "price_desc", label: "Price: High to Low" },
];

const YEARS = Array.from({ length: 30 }, (_, i) => String(2025 - i));

// Human-readable category from Pinnacle itemTypeCode e.g. FRONT_BUMPER -> Front Bumper
const formatCategory = (code) => {
  if (!code) return "";
  return code.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
};

// Small "jump to page" input — typing a number and pressing Enter goes straight there
function PageJumper({ page, totalPages, onJump }) {
  const [val, setVal] = useState("");
  const submit = (e) => {
    e.preventDefault();
    const n = parseInt(val, 10);
    if (n && n >= 1 && n <= totalPages) {
      onJump(n);
      setVal("");
    }
  };
  return (
    <form onSubmit={submit} className="flex items-center gap-2 text-[12px] text-zinc-500">
      <span>Jump to page</span>
      <input
        type="number" min={1} max={totalPages} value={val}
        onChange={(e) => setVal(e.target.value)}
        placeholder={String(page)}
        className="w-16 h-7 px-2 border border-zinc-300 rounded-sm text-[12px] text-center focus:outline-none focus:border-[#1e5bb8]"
      />
      <span>of {totalPages.toLocaleString()}</span>
      <button type="submit"
        className="px-2.5 h-7 border border-zinc-300 rounded-sm text-[12px] font-medium text-zinc-600 hover:border-[#1e5bb8] hover:text-[#1e5bb8] transition-colors">
        Go
      </button>
    </form>
  );
}

// Clean native select (no glass effect)
function NativeSelect({ value, onChange, disabled, children, className = "" }) {
  return (
    <div className={`relative ${className}`}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={`w-full h-9 pl-3 pr-8 text-[13px] font-medium text-zinc-800 bg-white border border-zinc-300 rounded-sm appearance-none focus:outline-none focus:border-[#1e5bb8] focus:ring-1 focus:ring-[#1e5bb8] transition-colors ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:border-zinc-400"}`}
        style={{ backgroundImage: "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e\")", backgroundPosition: "right 6px center", backgroundRepeat: "no-repeat", backgroundSize: "18px" }}
      >
        {children}
      </select>
    </div>
  );
}

// Search-as-you-type select for long lists like Make/Model (desktop only)
function TypeaheadSelect({ value, onChange, options, placeholder, disabled, dataTestId }) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const wrapRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const filtered = useMemo(() => {
    if (!term.trim()) return options;
    const t = term.trim().toLowerCase();
    return options.filter((o) => o.toLowerCase().includes(t));
  }, [options, term]);

  const select = (val) => {
    onChange(val);
    setTerm("");
    setOpen(false);
  };

  return (
    <div className={`relative ${disabled ? "opacity-50 pointer-events-none" : ""}`} ref={wrapRef}>
      <div
        onClick={() => !disabled && setOpen(true)}
        className={`w-full h-9 pl-3 pr-8 text-[13px] font-medium bg-white border rounded-sm flex items-center cursor-pointer transition-colors ${open ? "border-[#1e5bb8] ring-1 ring-[#1e5bb8]" : "border-zinc-300 hover:border-zinc-400"}`}>
        {open ? (
          <input
            autoFocus
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder={value || placeholder}
            className="w-full outline-none bg-transparent text-zinc-800 placeholder:text-zinc-400"
            data-testid={dataTestId}
          />
        ) : (
          <span className={value ? "text-zinc-800" : "text-zinc-400"}>{value || placeholder}</span>
        )}
      </div>
      <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-400 pointer-events-none" />
      {value && !open && (
        <button
          onClick={(e) => { e.stopPropagation(); select(""); }}
          className="absolute right-7 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-[#e31e24]">
          <X className="h-3 w-3" />
        </button>
      )}
      {open && (
        <div className="absolute z-30 top-full left-0 right-0 mt-1 bg-white border border-zinc-200 rounded-sm shadow-lg max-h-56 overflow-y-auto">
          <button
            onClick={() => select("")}
            className="w-full text-left px-3 py-2 text-[13px] text-zinc-500 hover:bg-zinc-50 border-b border-zinc-100">
            {placeholder}
          </button>
          {filtered.length > 0 ? filtered.map((opt) => (
            <button
              key={opt}
              onClick={() => select(opt)}
              className={`w-full text-left px-3 py-2 text-[13px] transition-colors ${value === opt ? "bg-[#0b1a3b] text-white font-semibold" : "text-zinc-700 hover:bg-red-50 hover:text-[#e31e24]"}`}>
              {opt}
            </button>
          )) : (
            <div className="px-3 py-2 text-[12px] text-zinc-400">No matches</div>
          )}
        </div>
      )}
    </div>
  );
}

// Collapsible accordion section
function AccordionSection({ title, icon, defaultOpen = true, badge, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-zinc-200">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-3 group"
        type="button">
        <span className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-500 group-hover:text-[#0b1a3b] transition-colors">
          {icon}
          {title}
          {badge ? <span className="text-[#e31e24] font-bold">{badge}</span> : null}
        </span>
        <ChevronDown className={`h-3.5 w-3.5 text-zinc-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="pb-4">{children}</div>}
    </div>
  );
}

export default function Products() {
  const [params, setParams] = useSearchParams();

  const [items,   setItems]   = useState([]);
  const [total,   setTotal]   = useState(0);
  const [facets,  setFacets]  = useState({ categories: [] });
  const [loading, setLoading] = useState(true);
  const [makes,   setMakes]   = useState([]);
  const [models,  setModels]  = useState([]);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [catSearch, setCatSearch] = useState("");
  const debounceRef = useRef(null);

  const q             = params.get("q")             || "";
  const category      = params.get("category")      || "";
  const vehicle_make  = params.get("vehicle_make")  || "";
  const vehicle_model = params.get("vehicle_model") || "";
  const vehicle_year  = params.get("vehicle_year")  || "";
  const in_stock      = params.get("in_stock") === "true";
  const sort          = params.get("sort")          || "newest";
  const page          = Number(params.get("page")   || 1);
  const pageSize      = Number(params.get("page_size") || 25);

  const up = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => {
      if (!v && v !== 0) next.delete(k); else next.set(k, String(v));
    });
    next.delete("page");
    setParams(next);
  };

  // Page/page-size navigation — does NOT reset page to 1, unlike up() which is
  // for filter changes. This was the root cause of "Next"/page-number clicks
  // silently resetting back to page 1.
  const goToPage = (n) => {
    const next = new URLSearchParams(params);
    next.set("page", String(Math.max(1, n)));
    setParams(next);
  };
  const setPageSize = (size) => {
    const next = new URLSearchParams(params);
    next.set("page_size", String(size));
    next.delete("page"); // changing page size restarts at page 1
    setParams(next);
  };

  const clearAll = () => {
    setSearchQ("");
    setCatSearch("");
    setParams(new URLSearchParams());
  };

  const activeCount = [q, category, vehicle_make, vehicle_model, vehicle_year, in_stock ? "1" : ""].filter(Boolean).length;

  useEffect(() => { setSearchQ(q); }, [q]);

  // Live search - debounced, fires automatically as user types (no Enter needed)
  useEffect(() => {
    if (searchQ === q) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      up({ q: searchQ });
    }, 350);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQ]);

  useEffect(() => {
    api.get("/vehicles/makes").then((r) => setMakes(r.data.makes || [])).catch(() => {});
  }, []);

  // Categories fetched once on mount — sidebar should always show all available
  // categories regardless of current filters, so users can switch between them.
  useEffect(() => {
    api.get("/products/facets")
      .then((f) => setFacets(f.data || { categories: [] }))
      .catch((err) => console.error("Facets fetch failed:", err));
  }, []);

  useEffect(() => {
    if (!vehicle_make) { setModels([]); return; }
    api.get(`/vehicles/${encodeURIComponent(vehicle_make)}/models`)
      .then((r) => setModels(r.data.models || []))
      .catch(() => setModels([]));
  }, [vehicle_make]);

  useEffect(() => {
    setLoading(true);
    const p = new URLSearchParams({ sort, page: String(page), page_size: String(pageSize) });
    if (q)             p.set("q", q);
    if (category)      p.set("category", category);
    if (vehicle_make)  p.set("vehicle_make", vehicle_make);
    if (vehicle_model) p.set("vehicle_model", vehicle_model);
    if (vehicle_year)  p.set("vehicle_year", vehicle_year);
    if (in_stock)      p.set("in_stock", "true");

    api.get(`/products?${p.toString()}`)
      .then((r) => {
        setItems(r.data.items || []);
        setTotal(r.data.total || 0);
        // Fire-and-forget search logging — only for actual typed searches,
        // not every filter change. Feeds the admin dashboard's top-searches
        // chart. Never blocks the UI or surfaces errors to the customer.
        if (q && q.trim()) {
          api.post("/track/search", {
            term: q.trim(),
            results_count: r.data.total || 0,
          }).catch(() => {});
        }
      })
      .catch((err) => console.error("Products fetch failed:", err))
      .finally(() => setLoading(false));
  }, [q, category, vehicle_make, vehicle_model, vehicle_year, in_stock, sort, page, pageSize]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const onSearch = (e) => {
    e.preventDefault();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    up({ q: searchQ });
  };

  // Real categories from Pinnacle data - human-readable, sorted, with counts
  const categoryList = useMemo(() => {
    const list = (facets.categories || [])
      .filter((c) => c && c.count > 0)
      .map((c) => ({ raw: c.name, label: formatCategory(c.name), count: c.count }))
      .sort((a, b) => a.label.localeCompare(b.label));
    if (!catSearch.trim()) return list;
    const term = catSearch.trim().toLowerCase();
    return list.filter((c) => c.label.toLowerCase().includes(term));
  }, [facets.categories, catSearch]);

  // page title
  let pageTitle = "All Parts";
  if (q)             pageTitle = `Search: "${q}"`;
  else if (vehicle_make && category) pageTitle = `${vehicle_make} ${formatCategory(category)}`;
  else if (vehicle_make) pageTitle = `${vehicle_make} Parts`;
  else if (category) pageTitle = formatCategory(category);

  return (
    <div className="bg-white min-h-screen" data-testid="products-page">
      <StoreHeader />

      {/* Page header - compact, clear hierarchy */}
      <div className="bg-[#0b1a3b] py-4">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="text-blue-300/70 text-[10px] font-mono uppercase tracking-widest mb-1 flex items-center gap-1.5">
                <Link to="/" className="hover:text-white transition-colors">Home</Link>
                <span>/</span>
                <span>All Parts</span>
                {category && <><span>/</span><span className="text-white">{formatCategory(category)}</span></>}
              </div>
              <h1 className="font-heading text-[22px] font-black uppercase text-white leading-tight">{pageTitle}</h1>
            </div>

            <div className="flex items-center gap-2 px-3 py-1.5 bg-[#e31e24] rounded-sm shrink-0">
              <span className="w-1.5 h-1.5 rounded-full bg-white/90 animate-pulse" />
              <span className="text-white font-heading font-black text-[15px] leading-none" data-testid="shop-live-count">
                {loading ? "..." : total.toLocaleString()}
              </span>
              <span className="text-white/85 text-[11px] font-mono uppercase tracking-wide leading-none">
                part{total !== 1 ? "s" : ""} live
              </span>
            </div>
          </div>
          {(vehicle_make || vehicle_model || vehicle_year) && (
            <p className="text-blue-300 text-[12px] mt-1">
              {vehicle_make && <span className="text-white font-medium">{vehicle_make}</span>}
              {vehicle_model && <span className="text-white font-medium"> {vehicle_model}</span>}
              {vehicle_year && <span className="text-white font-medium"> ({vehicle_year})</span>}
            </p>
          )}
        </div>
      </div>

      {/* Filter bar */}
      <div className="border-b border-zinc-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-3">

          <div className="hidden md:flex items-center gap-2 flex-wrap">

            <form onSubmit={onSearch} className="flex rounded-sm overflow-hidden border border-zinc-300 hover:border-[#1e5bb8] focus-within:border-[#1e5bb8] focus-within:ring-1 focus-within:ring-[#1e5bb8] transition-colors min-w-[220px] flex-1 max-w-sm relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-400 pointer-events-none" />
              <input
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                placeholder="Search part name or SKU..."
                className="flex-1 h-9 pl-8 pr-2 text-[13px] bg-white outline-none text-zinc-800 placeholder:text-zinc-400"
                data-testid="shop-live-search"
              />
              {searchQ && (
                <button type="button" onClick={() => setSearchQ("")}
                  className="px-2 text-zinc-400 hover:text-[#e31e24] transition-colors">
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </form>

            <div className="h-6 w-px bg-zinc-200" />

            <NativeSelect value={sort} onChange={(v) => up({ sort: v })} className="w-44" data-testid="shop-sort-select">
              {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </NativeSelect>

            <label className="flex items-center gap-1.5 cursor-pointer text-[13px] font-medium text-zinc-700 whitespace-nowrap select-none" data-testid="shop-in-stock-label">
              <input type="checkbox" checked={in_stock} onChange={(e) => up({ in_stock: e.target.checked ? "true" : "" })}
                className="w-4 h-4 rounded accent-[#1e5bb8] cursor-pointer" data-testid="shop-in-stock" />
              In stock only
            </label>

            {activeCount > 0 && (
              <button onClick={clearAll}
                className="flex items-center gap-1 text-[13px] font-medium text-[#e31e24] hover:text-[#b71820] border border-red-200 hover:border-red-300 bg-red-50 px-3 h-9 rounded-sm whitespace-nowrap transition-colors"
                data-testid="shop-clear-filters">
                <X className="h-3.5 w-3.5" /> Clear {activeCount > 0 && `(${activeCount})`}
              </button>
            )}
          </div>

          <div className="flex md:hidden items-center justify-between gap-3">
            <form onSubmit={onSearch} className="flex rounded-sm overflow-hidden border border-zinc-300 flex-1 relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-400 pointer-events-none" />
              <input value={searchQ} onChange={(e) => setSearchQ(e.target.value)} placeholder="Search parts..."
                className="flex-1 h-9 pl-8 pr-2 text-[13px] bg-white outline-none" />
            </form>
            <button onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)}
              className="flex items-center gap-1.5 h-9 px-3 border border-zinc-300 rounded-sm text-[13px] font-medium whitespace-nowrap">
              <SlidersHorizontal className="h-3.5 w-3.5" /> Filters
              {activeCount > 0 && <span className="w-4 h-4 bg-[#e31e24] text-white text-[10px] rounded-full flex items-center justify-center">{activeCount}</span>}
            </button>
          </div>

          {mobileFiltersOpen && (
            <div className="md:hidden mt-3 pt-3 border-t border-zinc-200 space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <NativeSelect value={vehicle_make} onChange={(v) => up({ vehicle_make: v, vehicle_model: "", vehicle_year: "" })}>
                  <option value="">All Makes</option>
                  {makes.map((m) => <option key={m} value={m}>{m}</option>)}
                </NativeSelect>
                <NativeSelect value={vehicle_model} onChange={(v) => up({ vehicle_model: v })} disabled={!vehicle_make}>
                  <option value="">{vehicle_make ? "All Models" : "Select make"}</option>
                  {models.map((m) => <option key={m} value={m}>{m}</option>)}
                </NativeSelect>
                <NativeSelect value={vehicle_year} onChange={(v) => up({ vehicle_year: v })}>
                  <option value="">All Years</option>
                  {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                </NativeSelect>
                <NativeSelect value={sort} onChange={(v) => up({ sort: v })}>
                  {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </NativeSelect>
              </div>
              <NativeSelect value={category} onChange={(v) => up({ category: v })}>
                <option value="">All Categories</option>
                {categoryList.map((c) => <option key={c.raw} value={c.raw}>{c.label} ({c.count})</option>)}
              </NativeSelect>
              <label className="flex items-center gap-2 text-sm font-medium text-zinc-700">
                <input type="checkbox" checked={in_stock} onChange={(e) => up({ in_stock: e.target.checked ? "true" : "" })}
                  className="w-4 h-4 accent-[#1e5bb8]" />
                In stock only
              </label>
            </div>
          )}
        </div>
      </div>

      {activeCount > 0 && (
        <div className="border-b border-zinc-100 bg-zinc-50">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-2 flex items-center gap-2 flex-wrap">
            <span className="text-[11px] font-mono uppercase tracking-widest text-zinc-400 mr-1">Active:</span>
            {q && <Chip label={`"${q}"`} onRemove={() => { setSearchQ(""); up({ q: "" }); }} />}
            {category && <Chip label={formatCategory(category)} onRemove={() => up({ category: "" })} />}
            {vehicle_make && <Chip label={vehicle_make} onRemove={() => up({ vehicle_make: "", vehicle_model: "", vehicle_year: "" })} />}
            {vehicle_model && <Chip label={vehicle_model} onRemove={() => up({ vehicle_model: "" })} />}
            {vehicle_year && <Chip label={vehicle_year} onRemove={() => up({ vehicle_year: "" })} />}
            {in_stock && <Chip label="In stock" onRemove={() => up({ in_stock: "" })} />}
            <button onClick={clearAll} className="text-[11px] text-[#e31e24] font-medium hover:underline ml-1">Clear all</button>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 flex gap-6">

        <aside className="hidden md:block w-60 shrink-0">

          <AccordionSection
            title="Find by Vehicle"
            icon={<Car className="h-3.5 w-3.5" />}
            defaultOpen={true}
            badge={(vehicle_make || vehicle_model || vehicle_year) ? "•" : null}>
            <div className="space-y-2">
              <TypeaheadSelect
                value={vehicle_make}
                onChange={(v) => up({ vehicle_make: v, vehicle_model: "", vehicle_year: "" })}
                options={makes}
                placeholder="All Makes"
                dataTestId="shop-make-select"
              />
              <TypeaheadSelect
                value={vehicle_model}
                onChange={(v) => up({ vehicle_model: v })}
                options={models}
                placeholder={vehicle_make ? "All Models" : "Select make first"}
                disabled={!vehicle_make}
                dataTestId="shop-model-select"
              />
              <TypeaheadSelect
                value={vehicle_year}
                onChange={(v) => up({ vehicle_year: v })}
                options={YEARS}
                placeholder="All Years"
                dataTestId="shop-year-select"
              />
              {(vehicle_make || vehicle_model || vehicle_year) && (
                <button onClick={() => up({ vehicle_make: "", vehicle_model: "", vehicle_year: "" })}
                  className="text-[11px] text-[#e31e24] hover:underline font-medium">
                  x Clear vehicle
                </button>
              )}
            </div>
          </AccordionSection>

          <AccordionSection
            title="Part Category"
            icon={<LayoutGrid className="h-3.5 w-3.5" />}
            defaultOpen={true}
            badge={category ? "•" : null}>
            <div className="space-y-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3 w-3 text-zinc-400 pointer-events-none" />
                <input
                  value={catSearch}
                  onChange={(e) => setCatSearch(e.target.value)}
                  placeholder="Search categories..."
                  className="w-full h-8 pl-7 pr-2 text-[12px] bg-zinc-50 border border-zinc-200 rounded-sm outline-none focus:border-[#1e5bb8] focus:bg-white transition-colors"
                />
              </div>
              <ul className="space-y-px max-h-[360px] overflow-y-auto pr-1">
                <li>
                  <button onClick={() => up({ category: "" })}
                    className={`w-full text-left text-[13px] px-2.5 py-1.5 transition-colors block rounded-sm ${!category ? "bg-[#0b1a3b] text-white font-bold" : "text-zinc-700 hover:text-[#e31e24] hover:bg-red-50"}`}>
                    All Categories
                  </button>
                </li>
                {categoryList.map((c) => (
                  <li key={c.raw}>
                    <button onClick={() => up({ category: c.raw })}
                      className={`w-full text-left text-[13px] px-2.5 py-1.5 transition-colors flex items-center justify-between rounded-sm ${category === c.raw ? "bg-[#0b1a3b] text-white font-bold" : "text-zinc-700 hover:text-[#e31e24] hover:bg-red-50"}`}
                      data-testid={`cat-filter-${c.raw}`}>
                      <span className="truncate">{c.label}</span>
                      <span className={`text-[11px] ml-2 shrink-0 ${category === c.raw ? "text-white/70" : "text-zinc-400"}`}>{c.count}</span>
                    </button>
                  </li>
                ))}
                {categoryList.length === 0 && (
                  <li className="text-[12px] text-zinc-400 px-2.5 py-2">No categories match "{catSearch}"</li>
                )}
              </ul>
            </div>
          </AccordionSection>
        </aside>

        <div className="flex-1 min-w-0">

          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="text-[13px] text-zinc-500">
              {loading ? "Loading..." : (
                <span>Showing <strong className="text-zinc-800">{total === 0 ? 0 : ((page - 1) * pageSize) + 1}-{Math.min(page * pageSize, total)}</strong> of <strong className="text-zinc-800">{total.toLocaleString()}</strong> parts</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[12px] text-zinc-500">Show:</span>
              {[25, 50, 100].map((n) => (
                <button key={n} onClick={() => setPageSize(n)}
                  className={`px-2.5 h-7 text-[12px] font-medium rounded-sm border transition-colors ${pageSize === n ? "bg-[#0b1a3b] border-[#0b1a3b] text-white" : "border-zinc-300 text-zinc-600 hover:border-[#1e5bb8] hover:text-[#1e5bb8]"}`}>
                  {n}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="rounded-sm overflow-hidden border border-zinc-100">
                  <div className="aspect-square bg-zinc-100 animate-pulse" />
                  <div className="p-4 space-y-2">
                    <div className="h-3 bg-zinc-100 rounded animate-pulse w-1/2" />
                    <div className="h-4 bg-zinc-100 rounded animate-pulse" />
                    <div className="h-4 bg-zinc-100 rounded animate-pulse w-3/4" />
                    <div className="h-6 bg-zinc-100 rounded animate-pulse w-1/3 mt-3" />
                  </div>
                </div>
              ))}
            </div>
          ) : items.length > 0 ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="products-grid">
                {items.map((p) => <ProductCard key={p.id} product={p} />)}
              </div>

              {totalPages > 1 && (
                <div className="mt-10 flex flex-col items-center gap-3" data-testid="products-pagination">
                  <div className="flex items-center justify-center gap-1.5 flex-wrap">
                    <button onClick={() => goToPage(1)} disabled={page === 1}
                      className="px-2.5 h-9 border border-zinc-300 text-[12px] font-medium text-zinc-700 disabled:opacity-40 hover:border-[#e31e24] hover:text-[#e31e24] rounded-sm transition-colors">
                      First
                    </button>
                    <button onClick={() => goToPage(page - 1)} disabled={page === 1}
                      className="flex items-center gap-1 px-3 h-9 border border-zinc-300 text-[13px] font-medium text-zinc-700 disabled:opacity-40 hover:border-[#e31e24] hover:text-[#e31e24] rounded-sm transition-colors">
                      <ChevronLeft className="h-4 w-4" /> Prev
                    </button>

                    {(() => {
                      // Sliding window of up to 5 page numbers centred on current page
                      const windowSize = 5;
                      let start = Math.max(1, page - Math.floor(windowSize / 2));
                      let end = Math.min(totalPages, start + windowSize - 1);
                      start = Math.max(1, end - windowSize + 1);
                      const nums = [];
                      for (let n = start; n <= end; n++) nums.push(n);
                      return (
                        <>
                          {start > 1 && <span className="text-zinc-400 px-1 text-[13px]">…</span>}
                          {nums.map((n) => (
                            <button key={n} onClick={() => goToPage(n)}
                              className={`w-9 h-9 text-[13px] font-medium rounded-sm border transition-colors ${page === n ? "bg-[#0b1a3b] border-[#0b1a3b] text-white" : "border-zinc-300 text-zinc-700 hover:border-[#e31e24] hover:text-[#e31e24]"}`}>
                              {n}
                            </button>
                          ))}
                          {end < totalPages && <span className="text-zinc-400 px-1 text-[13px]">…</span>}
                        </>
                      );
                    })()}

                    <button onClick={() => goToPage(page + 1)} disabled={page === totalPages}
                      className="flex items-center gap-1 px-3 h-9 border border-zinc-300 text-[13px] font-medium text-zinc-700 disabled:opacity-40 hover:border-[#e31e24] hover:text-[#e31e24] rounded-sm transition-colors">
                      Next <ChevronRight className="h-4 w-4" />
                    </button>
                    <button onClick={() => goToPage(totalPages)} disabled={page === totalPages}
                      className="px-2.5 h-9 border border-zinc-300 text-[12px] font-medium text-zinc-700 disabled:opacity-40 hover:border-[#e31e24] hover:text-[#e31e24] rounded-sm transition-colors">
                      Last
                    </button>
                  </div>

                  <PageJumper page={page} totalPages={totalPages} onJump={goToPage} />
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-24 border border-zinc-100 rounded-sm" data-testid="products-empty">
              <div className="font-heading text-[72px] font-black text-zinc-100 leading-none mb-4">0</div>
              <div className="font-heading font-bold text-[20px] text-zinc-400 uppercase mb-2">No parts found</div>
              <p className="text-[14px] text-zinc-400 mb-6 max-w-sm mx-auto">
                Try different filters, a different make/model, or contact us to source the part.
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                <button onClick={clearAll} className="px-5 py-2.5 bg-[#0b1a3b] text-white font-heading font-bold uppercase text-[13px] rounded-sm hover:bg-[#e31e24] transition-colors">
                  Clear all filters
                </button>
                <Link to="/quote" className="px-5 py-2.5 border border-zinc-300 text-zinc-700 font-heading font-bold uppercase text-[13px] rounded-sm hover:border-[#e31e24] hover:text-[#e31e24]">
                  Request a part
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>

      <StoreFooter />
    </div>
  );
}

function Chip({ label, onRemove }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#0b1a3b]/10 text-[#0b1a3b] text-[12px] font-medium rounded-sm border border-[#0b1a3b]/20">
      {label}
      <button onClick={onRemove} className="ml-0.5 hover:text-[#e31e24]">
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}